import React, { useState } from 'react';
import {
    Card,
    Button,
    Space,
    Typography,
    Input,
    DatePicker,
    Select,
    Form
} from 'antd';
import {
    ReloadOutlined,
    FilterOutlined,
    SearchOutlined
} from '@ant-design/icons';
import RequestList from '../components/requests/RequestList';
import {usePendingRequests} from "@/modules/action-flow/runtime/hooks/usePendingRequests";
import {ActionFlowRequestSearch} from "@/shared/api/model";

const {Title} = Typography;
const {RangePicker} = DatePicker;

export const RequestManagement: React.FC = () => {
    const [form] = Form.useForm();
    const [searchParams, setSearchParams] = useState<ActionFlowRequestSearch>({
        numberOfRows: 40,
        sortBy: 'scopeDate',
        orderBy: 'desc'
    });

    const { requests, isLoading, error, refetch } = usePendingRequests(searchParams);

    const handleSearch = (values: any) => {
        const newParams: ActionFlowRequestSearch = {
            ...searchParams,
            q: values.q,
            domain: values.domain,
            from: values.dateRange?.[0]?.format('YYYY-MM-DD'),
            to: values.dateRange?.[1]?.format('YYYY-MM-DD')
        };
        setSearchParams(newParams);
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({
            numberOfRows: 40,
            sortBy: 'scopeDate',
            orderBy: 'desc'
        });
    };

    const renderSearchForm = () => (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSearch}
            className="mb-4"
        >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Form.Item name="q" label="Search">
                    <Input
                        prefix={<SearchOutlined/>}
                        placeholder="Search records..."
                        allowClear
                    />
                </Form.Item>

                <Form.Item name="domain" label="Domain">
                    <Select
                        placeholder="Select domain"
                        allowClear
                        options={[
                            {label: 'Loans', value: 'LOANS'},
                            {label: 'Accounts', value: 'ACCOUNTS'},
                            {label: 'Customers', value: 'CUSTOMERS'}
                        ]}
                    />
                </Form.Item>

                <Form.Item name="dateRange" label="Date Range">
                    <RangePicker style={{width: '100%'}}/>
                </Form.Item>

                <Form.Item label=" " className="flex justify-end">
                    <Space>
                        <Button onClick={handleReset}>
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<FilterOutlined/>}
                        >
                            Filter
                        </Button>
                    </Space>
                </Form.Item>
            </div>
        </Form>
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={2}>Request Management</Title>
                <Button
                    icon={<ReloadOutlined/>}
                    onClick={() => refetch()}
                    loading={isLoading}
                >
                    Refresh
                </Button>
            </div>

            <Card>
                {renderSearchForm()}

                <RequestList
                    requests={requests}
                    isLoading={isLoading}
                    error={error instanceof Error ? error : null}
                    showResendAction={false}
                    hideSubmissionStep={false}
                    searchParams={searchParams}
                    onParamsChange={setSearchParams}
                />
            </Card>

            {/* Help Section */}
            <Card title="Help & Information" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Title level={4}>Pending Requests</Title>
                        <ul className="list-disc pl-4">
                            <li>This page shows requests awaiting your action</li>
                            <li>Review each request and take appropriate action</li>
                            <li>You can approve, reject, or return requests</li>
                        </ul>
                    </div>
                    <div>
                        <Title level={4}>Available Actions</Title>
                        <ul className="list-disc pl-4">
                            <li>View: See full request details</li>
                            <li>Approve: Accept the request</li>
                            <li>Reject: Decline the request</li>
                            <li>Return: Send back for changes</li>
                        </ul>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default RequestManagement;